import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export function PrivacyPolicy() {
  return (
    <div className="legal-page" data-testid="privacy-policy-page">
      <div className="legal-container">
        <Link to="/" className="legal-back-link" data-testid="back-link">
          <ArrowLeft size={18} />
          <span>Back</span>
        </Link>

        <div className="legal-header">
          <div className="legal-icon">
            <Shield size={24} />
          </div>
          <h1>Privacy Policy</h1>
          <p className="legal-updated">Last updated: January 2026</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to TradeLife. This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you use our software platform. We are committed to protecting your privacy 
              and handling your data transparently.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            <h3>Personal Information</h3>
            <p>We may collect information that you voluntarily provide:</p>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, password</li>
              <li><strong>Business Information:</strong> Organisation name, business details, client information</li>
              <li><strong>Financial Data:</strong> Invoice amounts, payment records, financial allocations</li>
              <li><strong>Communication Data:</strong> Notes, messages, AI assistant interactions</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <p>When you access the Service, we may automatically collect:</p>
            <ul>
              <li>Device type, operating system, unique device identifiers</li>
              <li>Features accessed, time spent, interaction patterns</li>
              <li>IP address, browser type, access times</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use collected information for:</p>
            <ul>
              <li><strong>Service Delivery:</strong> Providing and maintaining the platform</li>
              <li><strong>Account Management:</strong> Managing your account and authentication</li>
              <li><strong>AI Features:</strong> Powering AI-assisted tools within the platform</li>
              <li><strong>Notifications:</strong> Sending relevant alerts about your business activity</li>
              <li><strong>Improvement:</strong> Analysing usage to improve our Service</li>
              <li><strong>Support:</strong> Responding to inquiries and providing customer support</li>
              <li><strong>Legal Compliance:</strong> Complying with applicable laws and regulations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. AI Data Processing</h2>
            <p>
              Our AI features process your data according to strict guidelines:
            </p>
            <ul>
              <li>AI interactions are logged for audit and transparency purposes</li>
              <li>AI responses are based solely on your business data within the platform</li>
              <li>We do not use your data to train external AI models</li>
              <li>AI features are tools to assist you; they do not make decisions on your behalf</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage and Security</h2>
            <p>We implement industry-standard security measures:</p>
            <ul>
              <li>All data is stored on secure, encrypted servers</li>
              <li>We use encryption for data in transit (TLS/HTTPS)</li>
              <li>We implement access controls and authentication measures</li>
              <li>Regular security reviews are conducted</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Data Sharing</h2>
            <p>
              We do <strong>not</strong> sell your personal information. We may share data with:
            </p>
            <ul>
              <li><strong>Service Providers:</strong> Third parties that help us operate our Service (e.g., payment processors, hosting providers)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or asset sale</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Your Rights</h2>
            <p>Depending on your location, you may have rights to:</p>
            <ul>
              <li><strong>Access:</strong> Request copies of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data</li>
              <li><strong>Portability:</strong> Request transfer of your data</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
            </ul>
            <p>To exercise these rights, contact us at privacy@tradelife.app.</p>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention</h2>
            <p>
              We retain your data for as long as your account is active or as needed to provide services. 
              Financial records may be retained longer for legal and tax compliance purposes. You may request 
              deletion of your data at any time, subject to legal retention requirements.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 16. We do not knowingly collect personal 
              information from children.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes 
              by posting the new policy and updating the "Last Updated" date. Continued use of the Service 
              after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, contact us at:</p>
            <p className="legal-contact">
              <strong>Email:</strong> privacy@tradelife.app<br />
              <strong>Support:</strong> support@tradelife.app
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
